import { useCallback, useContext, useMemo, useState } from 'react';
import { ChooseTreatmentContext, Product, ProductItem } from '../FreeDosageLayoutV2';
import { RiDeleteBinLine } from "react-icons/ri";
import Disclaimer from '../../components/Disclaimers/Disclaimer';
import * as translator from '../../../../utils/translator';
import { DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT, TOOLTIP_MEDICATION, TOOLTIP_SERVICE_FEE, TOOLTIP_TREATMENT_FEE, TOOLTIP_SHIPPING_FEE } from '../../../../utils/consts';
import { isOrderRateLimited, getCannabisLimitMessage, officialPriceForProductItemSelfPickup, formatCannabisDisplayData, pricesForSelectedShippingMethod } from '../../helpersV2';
import DisclaimerTooltip from '../../../../components/DisclaimerTooltip';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { getPrice } from '../../../../utils/translator';
import * as constants from "../constants";
import AnimatedPopup from '../../../../components/AnimatedPopup/AnimatedPopup';
import { useCustomer } from '../../../../customHooks/useCustomer';
import { CANNABIS_SERVICE_FEE_TEST } from '../../../../utils/consts';
import ServiceFeeTestHelper from '../../../../components/ServiceFeeTestHelper';

type Props = {
  viewOrder?: boolean,
  setStickyNotification: (notification: { show: boolean; title: string; content: string; level: string; showInfoIcon: boolean; autoFade: boolean }) => void;
}

const ViewOrder: React.FC<Props> = ({ viewOrder, setStickyNotification }) => {

  const { customerState } = useCustomer();
  const chooseTreatmentContext = useContext(ChooseTreatmentContext)
  const setViewOrder = chooseTreatmentContext?.setViewOrder
  const productItems = chooseTreatmentContext?.selectedProductItems
  const selectedProducts = chooseTreatmentContext?.selectedProducts
  const maxCartProducts = chooseTreatmentContext?.maxCartProducts || 3
  const setSelectedProductItems = chooseTreatmentContext?.setSelectedProductItems;
  const selectedProductItems = chooseTreatmentContext?.selectedProductItems;
  const handleProductToggle = chooseTreatmentContext?.handleProductToggle
  const onSubmit = chooseTreatmentContext?.onSubmit
  const showExpressPrices = chooseTreatmentContext?.shippingMethod === constants.EXPRESS_SHIPPING;
  const stockCodesInRange = chooseTreatmentContext?.stockCodesInRange || [];
  const shippingMethod = chooseTreatmentContext?.shippingMethod;
  const calculateTotalMedicationPrice = chooseTreatmentContext?.calculateTotalMedicationPrice
  const calculateTotalShippingFee = chooseTreatmentContext?.calculateTotalShippingFee
  const calculateTotalServiceFee = chooseTreatmentContext?.calculateTotalServiceFee
  const serviceFeeTestInfo = chooseTreatmentContext?.getServiceFeeTestInfo?.()
  const consultationType = chooseTreatmentContext?.cart?.consultation_type;
  const selectedStockCode = chooseTreatmentContext?.selectedStockCode;
  const allPharmaciesLocations = chooseTreatmentContext?.allPharmaciesLocations;
  const submitLoading = chooseTreatmentContext?.submitLoading;
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [openedTooltips, setOpenedTooltips] = useState<Set<string>>(new Set());
  const language = localStorage.getItem("language") || "de";
  const locale = chooseTreatmentContext?.cart?.locale_id || 'de-DE';


  const rateLimited = useMemo(() => isOrderRateLimited(chooseTreatmentContext?.cannabisLimits ?? null), [chooseTreatmentContext?.cannabisLimits]);
  const cannabisLimitMessage = useMemo(() => getCannabisLimitMessage(chooseTreatmentContext?.cannabisLimits ?? null, language), [chooseTreatmentContext?.cannabisLimits, language]);

  const getShouldDisableUpButton = useCallback((product: Product, productItemIndex: number) => {
    if (rateLimited) return true;

    const currentQuantity = product.items[productItemIndex]?.quantity || 0;
    const nextQuantity = product.items[productItemIndex + 1]?.quantity || product.items[productItemIndex]?.quantity;


				return (chooseTreatmentContext?.totalQuantity || 0) - currentQuantity+ nextQuantity > (chooseTreatmentContext?.allowedCannabisGramsQuota || DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT);
			},

			[chooseTreatmentContext?.totalQuantity, chooseTreatmentContext?.allowedCannabisGramsQuota, rateLimited]
		);

    const reachedMaxQuota = useMemo(() => {
      if (rateLimited) return true;
      return selectedProductItems?.every(productItem => {
        const product = selectedProducts?.find(product => product.items.some(item => item.product_item_id === productItem.product_item_id));
        if (!product) return false;
        const productItemIndex = product?.items.findIndex(item => item.product_item_id === productItem.product_item_id) || 0;
        return getShouldDisableUpButton(product, productItemIndex);

        
      });
    }, [selectedProductItems, rateLimited]);


  const [lastClickedProductID, setLastClickedProductID] = useState<number | null>(null);


  
  const productByCatalogId = useMemo(() => {
    const map = new Map<number, Product>();
    (selectedProducts || []).forEach(p => map.set(p.catalog_id, p));
    return map;
  }, [selectedProductItems]);

  const getMaxAvailableAmountItem = (product: Product) => {
    const items = product.items;
    
    // Start from the highest quantity and work down to find the first available item
    if (shippingMethod === constants.EXPRESS_SHIPPING) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].express_stock) {
          return items[i];
        }
      }
    } else if (shippingMethod === constants.SHIPPING) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].in_stock) {
          return items[i];
        }
      }
    } else if (shippingMethod === constants.PICKUP) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].self_pickup_stock) {
          return items[i];
        }
      }
    }
    return items[0];
  }


  const nextProductItem = (index: number, product: Product) => {
    setLastClickedProductID(product.product_id);
    if (index < product.items.length - 1) {
      const currentItem = product.items[index];
      const isProductInStock = product.in_stock || product.express_stock || product.self_pickup_stock;
      const maxAvailableItem = getMaxAvailableAmountItem(product);

      // Prescription only logic
      if (!isProductInStock || chooseTreatmentContext?.treatAllAsRegular) {
        setSelectedProductItems!(currentProductItems => {
          return currentProductItems.map((pi) => {
            if (pi.catalog_id === product.catalog_id) {
              return { ...product.items[index + 1], preSelected: product.preSelected, selected_stock_code: pi.selected_stock_code };
            }
            return pi;
          })
        })
        return;
      }

      // Prescription + Medication logic
      if (currentItem.quantity < maxAvailableItem!.quantity) {
        setSelectedProductItems!(currentProductItems => {
          return currentProductItems.map((pi) => {
            if (pi.catalog_id === product.catalog_id) {
              let nextAvailableIndex = -1;
              if (shippingMethod === constants.EXPRESS_SHIPPING) {
                nextAvailableIndex = product.items.findIndex((item, idx) => idx > index && item.express_stock);
              } else if (shippingMethod === constants.SHIPPING) {
                nextAvailableIndex = product.items.findIndex((item, idx) => idx > index && item.in_stock);
              } else if (shippingMethod === constants.PICKUP) {
                nextAvailableIndex = product.items.findIndex((item, idx) => idx > index && item.self_pickup_stock);
              }
              if (nextAvailableIndex !== -1) {
                return { ...product.items[nextAvailableIndex], self_pickup_stock: pi.self_pickup_stock, preSelected: product.preSelected, selected_stock_code: pi.selected_stock_code };
              } else {
                return pi;
              }
            }
            return pi;
          })
        })
      }
    }
  }


  const previousProductItem = (index: number, product: Product) => {
    if (index > 0) {
      const isProductInStock = product.in_stock || product.express_stock || product.self_pickup_stock;

      // Prescription only logic
      if (!isProductInStock || chooseTreatmentContext?.treatAllAsRegular) {
        setSelectedProductItems!(currentProductItems => {
          return currentProductItems.map((pi) => {
            if (pi.catalog_id === product.catalog_id) {
              return { ...product.items[index - 1], preSelected: product.preSelected, selected_stock_code: pi.selected_stock_code };
            }
            return pi;
          })
        })
        return;
      }

      // Prescription + Medication logic
      setSelectedProductItems!(currentProductItems => {
        return currentProductItems.map((pi) => {
          if (pi.catalog_id === product.catalog_id) {
            // Prescription + Medication logic
            let targetIndex = -1;
            for (let i = index - 1; i >= 0; i--) {
              if (shippingMethod === constants.EXPRESS_SHIPPING) {
                if (product.items[i].express_stock) {
                  targetIndex = i;
                  break;
                }
              } else if (shippingMethod === constants.SHIPPING) {
                if (product.items[i].in_stock) {
                  targetIndex = i;
                  break;
                }
              } else if (shippingMethod === constants.PICKUP) {
                if (product.items[i].self_pickup_stock) {
                  targetIndex = i;
                  break;
                }
              }
            }
            if (targetIndex !== -1) {
              return { ...product.items[targetIndex], preSelected: product.preSelected, selected_stock_code: pi.selected_stock_code };
            }
            return pi;
          }
          return pi;
        })
      })
    }
  }


  const handleRemoveProductItem = (product: Product, productItem: ProductItem) => {
    handleProductToggle!({ selectedProduct: product, desiredProductItem: productItem, toggleAction: "REMOVE" })
    if (selectedProducts!.length === 1) {
      setViewOrder!(false);
    }
  }

  const handleSubmit = () => {
    try {
      onSubmit!();
    }
    catch (error) {
      setStickyNotification({ show: true, title: "Server Failed", content: "", level: "error", showInfoIcon: false, autoFade: false });
    }
  }

  // Calculate Cart Costs
  const treatmentFee = chooseTreatmentContext?.calculateTotalTreatmentFee!() || 0;
  const fastApprovalFee = chooseTreatmentContext?.calculateFastApprovalFee!() || 0;
  const totalShippingFee = calculateTotalShippingFee!();
  const totalServiceFee = calculateTotalServiceFee!();
  const consultationFee = chooseTreatmentContext?.cart?.consultation_fee!;
  const isVideoConsultation = consultationType === "video" && consultationFee;

  const medicationTotal = chooseTreatmentContext?.treatAllAsRegular ? 0 : calculateTotalMedicationPrice!();
  // Fast approval fee is included in treatment fee for invoice, but shown separately in order summary
  const totalAmount = medicationTotal + totalShippingFee + (isVideoConsultation ? consultationFee : treatmentFee + fastApprovalFee) + totalServiceFee;

  const numberOfProductsCustomerCanAdd = maxCartProducts - productItems!.length;


  const buildProductItemCard = (productItem: ProductItem) => {
    const product = productByCatalogId.get(productItem.catalog_id);
    if (!product) return null; // Defensive: if product not found, skip

    // Safer index calc
    const rawIdx = product.items.findIndex((item) => item.product_item_id === productItem?.product_item_id);
    const productItemIndex = Math.max(0, rawIdx);
    const isProductInStock = product.in_stock || product.express_stock || product.self_pickup_stock;

    const maxAvailableItem = getMaxAvailableAmountItem(product);
    const showDisclaimer =
      maxAvailableItem &&
      productItem?.quantity > maxAvailableItem.quantity &&
      !chooseTreatmentContext?.treatAllAsRegular &&
      isProductInStock &&
      product.items.length > 1;

      const isPreviousButtonDisabled = productItemIndex === product.items.findIndex((item) => item.in_stock);
      const isNextButtonDisabled = productItemIndex === product.items.length - 1 || (isProductInStock && !chooseTreatmentContext?.treatAllAsRegular && productItem.quantity >= maxAvailableItem!.quantity);

      const additionalDataObj = typeof product.additional_data === 'string'
        ? JSON.parse(product.additional_data)
        : product.additional_data || {};
      // cannabis data display processing
      const { thcDisplay, cbdDisplay, strain, shouldUseCannabisDosage } = formatCannabisDisplayData(additionalDataObj);
      const customDosageForCannabis = shouldUseCannabisDosage ? `THC: ${thcDisplay} | CBD: ${cbdDisplay}` : '';

      const pickupStockCode = productItem.selected_stock_code || selectedStockCode || '';
      const { packPrice: basePrice, minPricePerUnit: pricePerUnit } = pricesForSelectedShippingMethod({
        product,
        productItem,
        shippingMethod: shippingMethod || constants.SHIPPING,
        stockCodesInRange,
        selectedStockCode: pickupStockCode || selectedStockCode || null,
      });
      const selfPickupPrice = pickupStockCode
        ? officialPriceForProductItemSelfPickup(productItem, pickupStockCode)
        : basePrice;
      const selfPickupPricePerUnit = selfPickupPrice / productItem.quantity;

    return (
      <div id={`product-card-container`} key={productItem.product_item_id}
        className="flex flex-col w-full relative border-[1px] border-[#E4E4E4] border-solid rounded-lg p-4 gap-2">

        <div id='proudct-card-header' className='flex flex-col items-center justify-between gap-1'>

          <div className="flex flex-row w-full justify-between gap-1">
            <h2 className={`text-sm md:text-base font-semibold font-roboto text-[#0D2C54] ${!chooseTreatmentContext?.treatAllAsRegular && (productItem.in_stock || productItem.express_stock) ? 'max-w-[80%]' : 'max-w-[100%]'}`}>
              {product.title}
            </h2>

            {isProductInStock && !chooseTreatmentContext?.treatAllAsRegular &&
              <p className="text-lg font-medium font-roboto">{shippingMethod === constants.PICKUP ? getPrice(selfPickupPrice, locale) : getPrice(basePrice, locale)}</p>
            }
          </div>

          {/* Custom dosage text  */}
          {
            shouldUseCannabisDosage ?
              <div id='product-card-custom-dosage' className='flex flex-col justify-start w-full'>
                <div className='flex flex-col'>
                  <p id='product-card-custom-dosage-text' style={{ fontFamily: "Roboto, sans-serif" }} className='text-[13px] text-[#4D4D4D] font-medium'>{customDosageForCannabis}</p>
                  <p id='product-card-custom-dosage-strain' style={{ fontFamily: "Roboto, sans-serif" }} className='text-[13px] text-[#4D4D4D] font-medium'>{strain}</p>
                </div>
              </div>
              :
              null
          }

        </div>

        {/* Custom dosage text  */}

        <div id='product-card-body' className='flex flex-col items-center md:items-stretch gap-1'>

          {/* Selected Quantity Control */}
          <div className={`flex flex-row w-full items-center gap-1 ${!isProductInStock || chooseTreatmentContext?.treatAllAsRegular ? 'justify-end' : 'justify-between'}`}>

            {isProductInStock && !chooseTreatmentContext?.treatAllAsRegular ?
              <p className='flex justify-center font-medium text-xs text-[#0D2C54]'>
                {shippingMethod === constants.PICKUP ? getPrice(selfPickupPricePerUnit, locale) : getPrice(pricePerUnit, locale)} {translator.translate(language, 'choose_treatment', 'default/per_gram') || `pro ${productItem.custom_quantity_title.split(' ')?.[1]}`}
              </p>
              : null
            }
            {
              <div className="flex items-center gap-1 md:gap-3">
                <div id='product-card-quantity-control' style={{ border: "1px solid #E5E5E5" }} className='flex justify-between items-center rounded-md w-[140px] h-[46px] md:w-44 px-1.5 md:px-2'>
                  <div onClick={() => previousProductItem(productItemIndex, product)} className={`select-none rounded-md h-8 w-8 font-light text-3xl text-gray-500 text-center flex items-center justify-center
                    ${isPreviousButtonDisabled ? 'cursor-not-allowed bg-[#E7E7E7] opacity-75' : 'cursor-pointer bg-[#F7FAEF]'}`}>
                    -
                  </div>

                  <div className='flex flex-col justify-center items-center font-normal'>
                    <p className='text-[#0D2C54]'> {productItem?.quantity} </p>
                    <p className='text-[#98A0AF] text-xs'>{productItem.custom_quantity_title.split(' ')?.[1]}</p>
                  </div>

                  <div onClick={() => nextProductItem(productItemIndex, product)} className={`select-none rounded-md h-8 w-8 font-light text-3xl text-gray-500 text-center flex items-center justify-center
                          ${ isNextButtonDisabled ? 'cursor-not-allowed bg-[#E7E7E7] opacity-75' : 'cursor-pointer bg-[#F7FAEF]'}`}>
                    +
                  </div>
                </div>


                {/* Remove Button */}
                <button
                  id={`product-card-remove-button-${product.product_id}`}
                  onClick={() => {
                    handleRemoveProductItem(product, productItem)
                  }}
                  className='flex items-center justify-center w-[46px] h-[46px] rounded-md cursor-pointer bg-white'
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FAFAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  style={{ border: "1px solid #E5E5E5" }}
                >
                  <RiDeleteBinLine style={{ color: '#6B7280', width: '1.2rem', height: '1.2rem' }} />
                </button>
              </div>
            }
          </div>

          <div id='product-card-footer' className='flex flex-row justify-between md:gap-6'>
            {isProductInStock && !chooseTreatmentContext?.treatAllAsRegular &&
              <div id="delivery-info" className={`${showDisclaimer ? 'flex items-center' : ''}`}>

              {/* Max quota disclaimer */}
                {(lastClickedProductID === product.product_id || productItems?.length === 1) && reachedMaxQuota && (
                    <Disclaimer 
                    text = {rateLimited ? cannabisLimitMessage : translator.translate(language, 'choose_treatment', 'cannabis_limits/monthly_grams_quota').replace('<maxQuota>', String(chooseTreatmentContext?.cannabisLimits?.monthlyLimit || DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT)).replace('<quota>', String(chooseTreatmentContext?.allowedCannabisGramsQuota || DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT))}
                    containerClassName='md:max-w-[350px] max-w-[200px]'
                    textClassName='font-[400] text-xs items-center'
                    iconClassName='text-[#00B388] -mt-1'
                />
                )}
              </div>
            }
            {showDisclaimer && product.items?.length > 1 &&
              <Disclaimer
                text={translator.translate(language, "choose_treatment", "view_order/quantity_exceeds_stock") || "Menge überschreitet verfügbaren Bestand"}
                containerClassName='md:max-w-[350px] max-w-[200px]'
                textClassName='font-[400] text-xs items-center'
                iconClassName='text-[#00B388] -mt-1'
              />
            }
          </div>
        </div>
      </div>
    )
  }

  

  return (
    <AnimatedPopup
      isVisible={viewOrder || false}
      onClose={() => setViewOrder!(false)}
      title={translator.translate(language, "choose_treatment", "view_order/title") || "Artikel bestellen"}
    >
      <div id="view-order-container" className="flex flex-col gap-1 min-w-[300px] md:min-w-[400px]">
        <div
          id="product-items-container"
          className="flex flex-col max-h-[300px] md:max-h-[350px] gap-2 overflow-y-auto overflow-x-hidden"
        >
          {/* Product Items */}
          { shippingMethod !== constants.PICKUP && 
            productItems!.map((productItem) => {
              return buildProductItemCard(productItem);
            })
          }
          { shippingMethod === constants.PICKUP && 
            (() => {
              // Group product items by pharmacy (stock code)
              const pharmacyGroups = new Map<string, ProductItem[]>();
              
              selectedProductItems!.forEach((productItem) => {
                const stockCode = productItem.selected_stock_code || '';
                if (!pharmacyGroups.has(stockCode)) {
                  pharmacyGroups.set(stockCode, []);
                }
                pharmacyGroups.get(stockCode)!.push(productItem);
              });

              // Render pharmacy cards with their product items
              return Array.from(pharmacyGroups.entries()).map(([stockCode, items]) => {
                const pharmacy = allPharmaciesLocations?.[stockCode];
                if (!pharmacy) return null;

                return (
                  <div key={stockCode} className="w-full">
                    {/* Pharmacy Card */}
                    <div className="w-full text-left flex flex-col border border-solid border-[#E4E4E4] p-3 gap-2 rounded-lg bg-[#FBFBFB]">
                      <div className="flex-1 items-start">
                        <div className="font-semibold text-xs text-[#00302E]">
                          {pharmacy.name}
                        </div>
                        <div className="text-xs text-[#666666]">
                          {pharmacy.address}
                        </div>
                        {/* <div className="text-xs text-[#666666]">
                          {pharmacy.distance.toFixed(1).replace('.', ',')} {translator.translate(language, "choose_treatment", "default/km_from_you")}
                        </div> */}
                      </div>

                      {/* Product Items for this pharmacy */}
                      <div className="flex flex-col gap-2 items-center bg-[#FFFFFF]">
                        {items.map((productItem) => buildProductItemCard(productItem))}
                      </div>
                    </div>
                    
                  </div>
                );
              });
            })()
          }
          
        </div>


        {/* Shipping Cost Details */}
        <div id="view-order-costs-container" className="flex flex-col gap-1 text-base text-[#656D79] font-light">

          {numberOfProductsCustomerCanAdd > 0 && !reachedMaxQuota && (
            <div
              className="border-0 border-l-4 border-border-focus border-solid rounded-md my-1 p-2 pl-4 flex justify-between items-center cursor-pointer bg-[#F4FDFA]"
              onClick={() => {
                // Track add_third_product_click event (only when current count = 2)
                const currentCount = productItems!.length;
                if (currentCount === 2) {
                  MainTracker.track("custom", "add_third_product_click", {
                    user_id: customerState?.customerID || null,
                    current_cart_products_count: currentCount
                  });
                }
                setViewOrder!(false);
              }}
            >
              <p className="text-base text-[#656D79] font-roboto max-w-[80%]">
                {(numberOfProductsCustomerCanAdd === 1
                  ? translator.translate(language, "choose_treatment", "cannabis_limits/can_add_more_singular")
                  : (translator.translate(language, "choose_treatment", "cannabis_limits/can_add_more_plural") || "").replace("<count>", String(numberOfProductsCustomerCanAdd))
                ) || `Sie können ${numberOfProductsCustomerCanAdd} weitere Medikamente zu diesem Rezept hinzufügen.`}
              </p>
              <div className="bg-primary-button-gradient rounded-lg px-4 py-2 flex items-center justify-center min-w-[48px] h-12">
                <span className="text-white text-2xl font-normal">+</span>
              </div>
            </div>
          )}

          <div id="view-order-costs-breakdown" className="flex flex-col">

            {
              !chooseTreatmentContext?.treatAllAsRegular &&
              <div id="medication-total-cost" className="flex justify-between">
                <div className='flex items-center gap-2'>
                  <span className='font-roboto'>{translator.translate(language, "choose_treatment", "view_order/medication_cannabis_catalog")}:</span>
                  <DisclaimerTooltip
                    isOpen={openTooltip === TOOLTIP_MEDICATION}
                    onToggle={() => {
                      const newState = openTooltip === TOOLTIP_MEDICATION ? null : TOOLTIP_MEDICATION;
                      setOpenTooltip(newState);
                      if (newState === TOOLTIP_MEDICATION) {
                        setOpenedTooltips(prev => new Set(prev).add(TOOLTIP_MEDICATION));
                      }
                    }}
                    tooltipText={translator.translate(language, 'default', 'OrderWindow/tooltip_medication')}
                    tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
                    positionAbove
                  />
                </div>
                <span className='font-roboto'>{getPrice(medicationTotal, locale)}</span>
              </div>
              
            }
            <div id="treatment-fee" className="flex justify-between">
              {(() => {
                return (
                  <>
                    <div className='flex items-center gap-2'>
                      <span className='font-roboto'>
                        {isVideoConsultation
                          ? translator.translate(
                              language,
                              "choose_treatment",
                              chooseTreatmentContext?.cart?.is_follow_up_consultation
                                ? "view_order/video_consultation_fee_follow_up"
                                : "view_order/video_consultation_fee"
                            )
                          : translator.translate(language, "choose_treatment", "view_order/treatment_fee")
                        }:
                      </span>
                      <DisclaimerTooltip
                        isOpen={openTooltip === TOOLTIP_TREATMENT_FEE}
                        onToggle={() => {
                          const newState = openTooltip === TOOLTIP_TREATMENT_FEE ? null : TOOLTIP_TREATMENT_FEE;
                          setOpenTooltip(newState);
                          if (newState === TOOLTIP_TREATMENT_FEE) {
                            setOpenedTooltips(prev => new Set(prev).add(TOOLTIP_TREATMENT_FEE));
                          }
                        }}
                        tooltipText={translator.translate(language, 'default', 'OrderWindow/tooltip_treatment_fee')}
                        tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
                        positionAbove
                      />
                    </div>
                    <span className='font-roboto'>{
                      isVideoConsultation 
                        ? getPrice(chooseTreatmentContext?.cart?.consultation_fee || 0, locale)
                        : getPrice(treatmentFee, locale)
                    }</span>
                  </>
                );
              })()}
            </div>
            
            {/* Fast Doctor Approval Fee - shown as separate line item */}
            {fastApprovalFee > 0 && !isVideoConsultation && (
              <div id="fast-approval-fee" className="flex justify-between">
                <span className='font-roboto'>
                  {translator.translate(language, "choose_treatment", "view_order/fast_doctor_approval")}:
                </span>
                <span className='font-roboto'>{getPrice(fastApprovalFee, locale)}</span>
              </div>
            )}
            
            { totalServiceFee > 0 && (
            <div id="service-fee-wrapper">
              <div id="service-fee" className="flex justify-between">
                <div className='flex items-center gap-2'>
                  <span className='font-roboto'>
                    {translator.translate(language, "choose_treatment", "view_order/service_fee")}:
                  </span>
                  <DisclaimerTooltip
                    isOpen={openTooltip === TOOLTIP_SERVICE_FEE}
                    onToggle={() => {
                      const newState = openTooltip === TOOLTIP_SERVICE_FEE ? null : TOOLTIP_SERVICE_FEE;
                      setOpenTooltip(newState);
                      if (newState === TOOLTIP_SERVICE_FEE) {
                        setOpenedTooltips(prev => new Set(prev).add(TOOLTIP_SERVICE_FEE));
                      }
                    }}
                    tooltipText={serviceFeeTestInfo?.isVariantC && !serviceFeeTestInfo?.nonFlowerPresent
                      ? translator.translate(language, 'default', 'OrderWindow/tooltip_service_fee_test')
                          .replace(':flowerFee1', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT_C[1], locale))
                          .replace(':flowerFee2', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT_C[2], locale))
                          .replace(':flowerFee3', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT_C[3], locale))
                          .replace(':nonFlowerFee', getPrice(CANNABIS_SERVICE_FEE_TEST.NON_FLOWER_FEE, locale))
                      : serviceFeeTestInfo?.isVariantB
                      ? translator.translate(language, 'default', 'OrderWindow/tooltip_service_fee_test')
                          .replace(':flowerFee1', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT[1], locale))
                          .replace(':flowerFee2', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT[2], locale))
                          .replace(':flowerFee3', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT[3], locale))
                          .replace(':nonFlowerFee', getPrice(CANNABIS_SERVICE_FEE_TEST.NON_FLOWER_FEE, locale))
                      : translator.translate(language, 'default', 'OrderWindow/tooltip_service_fee')}
                    tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
                    positionAbove
                  />
                </div>
                <span className='font-roboto'>{ getPrice(totalServiceFee, locale)}</span>
              </div>
              <ServiceFeeTestHelper
                variant={serviceFeeTestInfo?.variant}
                flowerCount={serviceFeeTestInfo?.flowerCount ?? 0}
                nonFlowerPresent={serviceFeeTestInfo?.nonFlowerPresent ?? false}
                language={language}
                id="service-fee-test-helper"
                className="text-[#656D79] text-[12px] font-roboto mt-0.5 whitespace-nowrap"
              />
            </div>
)}

            {
              !chooseTreatmentContext?.treatAllAsRegular && totalShippingFee > 0 &&
              <div id="shipping-fee" className="flex justify-between">
                <div className='flex items-center gap-2'>
                  <span className='font-roboto'>{translator.translate(language, "choose_treatment", "view_order/shipping")}:</span>
                  {showExpressPrices && chooseTreatmentContext?.selectedExpressTier !== 'same_day' && ( 
                    <DisclaimerTooltip
                      isOpen={openTooltip === TOOLTIP_SHIPPING_FEE}
                      onToggle={() => {
                        const newState = openTooltip === TOOLTIP_SHIPPING_FEE ? null : TOOLTIP_SHIPPING_FEE;
                        setOpenTooltip(newState);
                        if (newState === TOOLTIP_SHIPPING_FEE) {
                          setOpenedTooltips(prev => new Set(prev).add(TOOLTIP_SHIPPING_FEE));
                        }
                      }}
                      tooltipText={(translator.translate(language, "default", "OrderWindow/express_delivery_price_tooltip") ?? '')
                        .replace(':deliveryFee', getPrice(Math.max(0, totalShippingFee - (chooseTreatmentContext?.fastApprovalFeeFromServer ?? 0)), locale, 1))
                        .replace(':doctorApprovalFee', getPrice(chooseTreatmentContext?.fastApprovalFeeFromServer ?? 0, locale, 1))}
                      tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
                      positionAbove
                    />
                  )}
                </div>

                <span className='font-roboto'>{getPrice(totalShippingFee, locale)}</span>
              </div>
            }

            </div>

        </div>

        <div id="total-order-cost" className="pb-2 flex justify-between items-center text-lg text-[#0D2C54] border-b">
          <span className='font-roboto font-extrabold'>{translator.translate(language, "choose_treatment", "view_order/total")}:</span>
          <span className='font-roboto font-bold'>{getPrice(totalAmount, locale)}</span>
        </div>


        {/* Go to Shipping */}
        <button
          id="submit-choose-treatment"
          onClick={() => {
            if (submitLoading) return;
            
            // Track user click breakdown event
            MainTracker.track("custom", "user click breakdown", {
              "page": "Choose Treatment",
              "Click on delivery options": false,
              "Click on Medication tooltip": openedTooltips.has(TOOLTIP_MEDICATION),
              "Click on Service Fee tooltip": openedTooltips.has(TOOLTIP_SERVICE_FEE),
              "Click on Treatment Fee tooltip": openedTooltips.has(TOOLTIP_TREATMENT_FEE),
              "Click expand product (show/hide)": false,
              "Click on Partner Pharmacies pop-up": false,
              "Click on pharmacy details (expand)": false,
              "Click on terms": false,
              "Click on doctor details tooltip": false,
              "Click on product pharmacy tooltip": false
            });

            handleSubmit();
          }}
          disabled={selectedProducts!.length === 0 || submitLoading}
          className={`w-full border-none ${selectedProducts!.length === 0 ? `cursor-not-allowed bg-primary-button-gradient-hover` : submitLoading ? `cursor-wait bg-primary-button-gradient-hover` : `bg-primary-button-gradient cursor-pointer hover:opacity-90`}
              text-base text-white rounded-md py-4 font-poppins font-bold`}
        >
          {translator.translate(language, "choose_treatment", "view_order/submit")}
        </button>

        {/* Back To Product Selection */}
        <div
          className="text-[#98A0AF] cursor-pointer text-center hover:underline pb-6 pt-2 md:pb-0"
          onClick={() => {
            setViewOrder!(false)
          }}
        >
            {translator.translate(language, "choose_treatment", "view_order/close")}
        </div>
      </div>
    </AnimatedPopup>
  );
};

export default ViewOrder;
